import { useEffect, useMemo, useRef, useState } from 'react';
import {
  usePaginatedAssetCategories,
  useDeleteAssetCategory,
  useBulkDeleteAssetCategories,
} from '../../../services/httpServices/queries';
import { useDebounce } from '../../../hooks/useDebounce';
import { Pagination } from '../../../components/ui/Pagination';
import { ConfirmDialog } from '../../../components/ui/confirm-dialog';
import { FixedAssetsLayout } from './FixedAssetsLayout';
import { ExpensesLayout } from '../expenses/ExpensesLayout';
import { AssetCategoryFormModal } from './AssetCategoryFormModal';
import { AssetCategoryDetailsModal } from './AssetCategoryDetailsModal';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  X,
  Loader2,
  AlertCircle,
  Pencil,
  Trash2,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import type { AssetCategory, CategoryScope } from '../../../types/asset';

interface AssetCategoriesProps {
  /** ASSET (Assets → Categories) or EXPENSE (Expenses → Categories). */
  scope?: CategoryScope;
}

export function AssetCategories({ scope = 'ASSET' }: AssetCategoriesProps) {
  const limit = 20;
  const isExpense = scope === 'EXPENSE';
  const Layout = isExpense ? ExpensesLayout : FixedAssetsLayout;

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [confirm, setConfirm] = useState<AssetCategory | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AssetCategory | null>(null);
  const [viewing, setViewing] = useState<AssetCategory | null>(null);

  const { data, isLoading, error } = usePaginatedAssetCategories(
    page,
    limit,
    debouncedSearch || undefined,
    scope,
  );
  const categories = useMemo(() => data?.data || [], [data]);
  const meta = data?.meta;
  const deleteCategory = useDeleteAssetCategory();
  const bulkDeleteCategories = useBulkDeleteAssetCategories();

  // Row selection for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  // Drop selected ids that are no longer visible (e.g. after paging/filtering).
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(categories.map((category) => category.id));
      const next = new Set<string>();
      let changed = false;
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [categories]);

  const allSelected = categories.length > 0 && categories.every((category) => selectedIds.has(category.id));
  const someSelected = categories.some((category) => selectedIds.has(category.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  // If the current page becomes empty (e.g. after deleting its rows), step back.
  useEffect(() => {
    if (!isLoading && categories.length === 0 && page > 1) {
      setPage((prev) => Math.max(1, prev - 1));
    }
  }, [isLoading, categories.length, page]);

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const everySelected =
        categories.length > 0 && categories.every((category) => prev.has(category.id));
      return everySelected ? new Set() : new Set(categories.map((category) => category.id));
    });
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (category: AssetCategory) => {
    setViewing(null);
    setEditing(category);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };

  const handleDelete = async () => {
    if (!confirm) return;
    try {
      await deleteCategory.mutateAsync(confirm.id);
      toast.success('Category deleted');
    } catch {
      toast.error('Failed to delete category');
    } finally {
      setConfirm(null);
    }
  };

  const handleConfirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    try {
      await bulkDeleteCategories.mutateAsync(ids);
      setSelectedIds(new Set());
      toast.success(`Deleted ${ids.length} categor${ids.length === 1 ? 'y' : 'ies'}`);
    } catch {
      toast.error('Failed to delete some categories');
    } finally {
      setBulkConfirmOpen(false);
    }
  };

  return (
    <Layout
      title={isExpense ? 'Expense Categories' : 'Asset Categories'}
      description={
        isExpense
          ? 'Group operating expenses (rent, utilities, supplies, ...)'
          : 'Group fixed assets (medical equipment, furniture, vehicles, ...)'
      }
      helpKey={isExpense ? '/billing/expenses/categories' : '/billing/assets/categories'}
      action={
        <button
          onClick={openCreate}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
        >
          <Plus size={16} />
          Add Category
        </button>
      }
    >
      <div className="bg-surface rounded-xl border border-border p-4">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search categories..."
            className="w-full pl-9 pr-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.size}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkConfirmOpen(true)}
              disabled={bulkDeleteCategories.isPending}
              className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkDeleteCategories.isPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Trash2 size={14} />
              )}
              Delete
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkDeleteCategories.isPending}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      <div className="bg-surface rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-background/50">
                <th className="px-4 py-3 w-10 text-left">
                  <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={categories.length === 0}
                    title="Select all"
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Description</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-text-muted uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-danger">
                    <AlertCircle size={18} className="inline mr-2" /> Failed to load categories
                  </td>
                </tr>
              ) : categories.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-text-muted">
                    {search ? (
                      <span>
                        No categories match your search.{' '}
                        <button onClick={() => setSearch('')} className="text-primary hover:underline">
                          Clear search
                        </button>
                      </span>
                    ) : (
                      'No categories found. Add your first category to get started.'
                    )}
                  </td>
                </tr>
              ) : (
                categories.map((category) => (
                  <tr key={category.id} className="hover:bg-background/50 transition-colors">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(category.id)}
                        onChange={() => toggleOne(category.id)}
                        className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => setViewing(category)}
                        className="font-medium text-text hover:text-primary transition-colors text-left"
                        title="View details"
                      >
                        {category.name}
                      </button>
                    </td>
                    <td className="px-4 py-3 font-mono text-text-muted">{category.code || '—'}</td>
                    <td className="px-4 py-3 text-text-muted">{category.description || '—'}</td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          category.isActive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                        }`}
                      >
                        {category.isActive ? <CheckCircle size={12} /> : <XCircle size={12} />}
                        {category.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => openEdit(category)}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                          title="Edit"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirm(category)}
                          disabled={deleteCategory.isPending}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors disabled:opacity-50"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          page={page}
          totalPages={meta?.totalPages || 1}
          total={meta?.total || 0}
          limit={limit}
          onPageChange={setPage}
          itemName="categories"
        />
      </div>

      <AssetCategoryFormModal
        open={formOpen}
        onClose={closeForm}
        category={editing}
        scope={scope}
      />

      <AssetCategoryDetailsModal
        open={viewing !== null}
        category={viewing}
        onClose={() => setViewing(null)}
        onEdit={openEdit}
      />

      <ConfirmDialog
        open={confirm !== null}
        title="Delete category"
        description={
          <>
            Delete <strong>{confirm?.name}</strong>? Existing records keep their value but lose this category.
          </>
        }
        loading={deleteCategory.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
      />

      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Delete ${selectedIds.size} categor${selectedIds.size === 1 ? 'y' : 'ies'}?`}
        description="Existing records keep their value but lose these categories. This cannot be undone."
        confirmLabel={`Delete ${selectedIds.size} categor${selectedIds.size === 1 ? 'y' : 'ies'}`}
        loading={bulkDeleteCategories.isPending}
        onConfirm={handleConfirmBulkDelete}
        onCancel={() => setBulkConfirmOpen(false)}
      />
    </Layout>
  );
}
